import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import Reveal from "../components/Reveal";
import StarfieldCanvas from "../components/StarfieldCanvas";
import MaskedLines from "../components/MaskedLines";
import FaqSection from "../components/FaqSection";
import { SHOP_URL, FAQ_CATEGORIES } from "../data/content";

export default function FAQ() {
  usePageMeta(
    "FAQ — Consciousness Ascending",
    "Common questions about our content, guidebooks, remote viewing, astral projection, and purchasing through the Fourthwall shop."
  );
  return (
    <>
      <header className="relative overflow-hidden border-b border-white/[0.06]">
        <StarfieldCanvas className="absolute inset-0 h-full w-full" interactive={false} />
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(139,92,246,0.12),transparent_55%)]"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-5 pb-16 pt-32 sm:px-8 sm:pb-20 sm:pt-40 lg:px-10 lg:pt-44">
          <p className="eyebrow">Frequently Asked Inquiries</p>
          <h1 className="font-display mt-6 font-light leading-[1.05] tracking-tight text-[clamp(2.1rem,6.5vw,4rem)]">
            <MaskedLines lines={["Before you", "ask the void."]} />
          </h1>
          <Reveal delay={0.5}>
            <p className="body-muted mt-8 max-w-xl text-base sm:text-lg">
              On the content, the guidebooks, both disciplines, and how
              purchasing works. Anything unanswered usually lives inside a
              manual.
            </p>
          </Reveal>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10" data-testid="faq-page-sections">
        {FAQ_CATEGORIES.map((cat, i) => (
          <section
            key={cat.name}
            className={`py-16 lg:py-20 ${i !== 0 ? "border-t border-white/[0.06]" : ""}`}
          >
            <Reveal>
              <div className="flex items-baseline gap-5">
                <span className="font-mono-ui text-[11px] tracking-[0.22em] text-[#38BDF8]">
                  0{i + 1}
                </span>
                <h2 className="font-display text-2xl font-light tracking-tight text-[#F0F2F8] sm:text-3xl">
                  {cat.name}
                </h2>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="mt-10">
              <FaqSection items={cat.faqs} />
            </Reveal>
          </section>
        ))}
      </div>

      <section className="border-t border-white/[0.07] bg-[#0C0E14]/40">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-5 py-16 sm:px-8 lg:flex-row lg:items-center lg:px-10 lg:py-20">
          <Reveal>
            <p className="font-display text-2xl font-light tracking-tight text-[#F0F2F8] sm:text-3xl">
              Ready to hold the manual instead?
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/guidebooks"
                data-testid="faq-guidebooks-btn"
                className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#F0F2F8] transition-all duration-300 hover:border-[#8B5CF6]"
              >
                Browse guidebooks <ArrowRight className="h-3.5 w-3.5 text-[#A78BFA]" />
              </Link>
              <a
                href={SHOP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="faq-shop-btn"
                className="inline-flex items-center gap-2 border border-[#8B5CF6] bg-[#8B5CF6]/15 px-6 py-3.5 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#F0F2F8] transition-all duration-300 hover:bg-[#8B5CF6]/30"
              >
                Fourthwall Shop <ArrowUpRight className="h-3.5 w-3.5 text-[#A78BFA]" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
